import React, { useCallback } from 'react';
import { ImageBackground, View, type ImageSourcePropType } from 'react-native';
import Text from '../Text';
import Button from '../Button';
import type { RootStackParamList } from '~/navigation/types';
import type { SvgProps } from 'react-native-svg';
import useAppNavigation from '~/hooks/useAppNavigation';
import type { InsuranceType } from '~/@types/insurance';
import { MarisaAppUtils } from '~/utils/MarisaAppUtils';
import useHeader from '~/hooks/useHeader';

interface ShowButtonProps {
  showButton: true;
  type: InsuranceType;
  redirectScreen?: keyof RootStackParamList;
  onPressButton?: () => void;
  label?: string;
}

interface HideButtonProps {
  showButton: false;
}

interface BannerImageList {
  mobile: ImageSourcePropType;
  web: {
    banner: ImageSourcePropType;
    badge?: React.FC<SvgProps>;
  };
}

interface Props {
  insuranceName: string;
  insuranceDetail: string;
  obs?: string;
  images: BannerImageList;
  hireInsurance: ShowButtonProps | HideButtonProps;
  disabled?: boolean;
}

const OfferBanner: React.FC<Props> = ({
  images,
  insuranceName,
  insuranceDetail,
  obs,
  hireInsurance,
  disabled,
}) => {
  const { navigate } = useAppNavigation();
  const { isLoggedIn } = useHeader();

  const onNavigate = useCallback(() => {
    if (hireInsurance.showButton && hireInsurance.redirectScreen) {
      if (
        ['totem', 'crediario'].includes(MarisaAppUtils.origin) &&
        !isLoggedIn
      ) {
        navigate('MarisaServicos_NoAuthContractPage', {
          type: hireInsurance.type,
          navigateTo: hireInsurance.redirectScreen,
        });
      } else {
        navigate(hireInsurance.redirectScreen as any);
      }
    }
  }, [hireInsurance, isLoggedIn, navigate]);

  const RenderBadge = useCallback(() => {
    if (images.web.badge) {
      const {
        web: { badge: Badge },
      } = images;

      return <Badge />;
    }
    return null;
  }, [images]);

  return (
    <>
      <ImageBackground
        source={images.mobile}
        resizeMode="cover"
        className={`w-full md:hidden ${
          hireInsurance.showButton ? 'h-48' : 'h-40'
        } justify-center md:rounded-2xl md:mt-24 flex sm:hidden`}
      >
        <View className="pl-6 w-[65%]">
          <Text className="text-light-100 text-lg font-bold">
            {insuranceName}
          </Text>

          <Text className="text-light-100 mt-2 text-sm">{insuranceDetail}</Text>
          <Text className="text-light-100 text-[10px]">{obs}</Text>

          {hireInsurance.showButton && (
            <Button
              className="mr-auto px-4 py-2 rounded-2xl mt-4"
              onPress={
                disabled
                  ? () => null
                  : hireInsurance.onPressButton || onNavigate
              }
            >
              {disabled ? 'Em breve' : hireInsurance.label || 'CONTRATE'}
            </Button>
          )}
        </View>
      </ImageBackground>

      <ImageBackground
        source={images.web.banner}
        resizeMode="cover"
        imageStyle={{ borderRadius: 16 }}
        className={`w-full mt-16 h-[266px] p-6 hidden sm:flex`}
      >
        <View
          className={`w-[55%] h-full ${
            images.web.badge ? 'justify-between' : 'justify-center'
          }`}
        >
          <RenderBadge />

          <View className="mt-2">
            <Text className="text-light-100 text-lg font-bold">
              {insuranceName}
            </Text>

            <Text className="text-light-100 mt-2 text-sm">
              {insuranceDetail}
            </Text>
            <Text className="text-light-100 text-[12px]">{obs}</Text>
          </View>

          {hireInsurance.showButton && (
            <Button
              className="mr-auto px-4 py-2 rounded-2xl mt-4"
              onPress={
                disabled
                  ? () => null
                  : hireInsurance.onPressButton || onNavigate
              }
            >
              {disabled ? 'Em breve' : hireInsurance.label || 'CONTRATE'}
            </Button>
          )}
        </View>
      </ImageBackground>
    </>
  );
};

export default OfferBanner;
